import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  List,
  Loader2,
  MoreHorizontal,
  Settings2,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { hasPermission, useClaims } from "@/hooks/use-claims";
import type { TFunction, TParams } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { useAdminOrgStore } from "@/stores/admin-org";

import { LoadError, PickASite, RecipeCount, RowsSkeleton, useOpen } from "./menu-parts";
import {
  GROUPS,
  datesLabel,
  daysBetween,
  groupOf,
  hasEnded,
  inDates,
  isLiveAt,
  liveFromAt,
  isReading,
  menuLabel,
  predecessorOf,
  runsAt,
  serviceTime,
  shortDate,
  sortGroup,
  type Menu,
  type MenuGroup,
} from "./menu-model";
import { MenuSettingsDialog } from "./menu-settings-dialog";
import { UploadFlow, stillReading } from "./upload-flow";
import { useMenuActions, useMenuDetail, useSiteMenus, useSiteToday } from "./use-menus";

/**
 * The Menus list (POS2-1456): one site's menus grouped by how they run, each with a live switch
 * and a settings menu, and the platform default as the fallback at the foot.
 */

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

const GROUP_LABELS: Record<MenuGroup, { label: string; hint: string }> = {
  always_on: { label: "menus.cadence.always_on", hint: "menus.groups.alwaysOnHint" },
  cycle: { label: "menus.cadence.cycle", hint: "menus.groups.cycleHint" },
  one_off: { label: "menus.cadence.one_off", hint: "menus.groups.oneOffHint" },
  ended: { label: "menus.groups.ended", hint: "menus.groups.endedHint" },
};

interface DefaultMenu {
  name: string;
  recipe_count: number;
}

export default function MenusPage() {
  const { t } = useTranslation();
  const siteId = useAdminOrgStore((s) => s.siteId);
  const siteName = useAdminOrgStore((s) => s.siteName);
  const today = useSiteToday(siteId);
  if (!siteId || today === null) {
    return (
      <div className="mx-auto max-w-5xl">
        <h1 className="mb-6 text-lg font-medium text-gray-900">{t("menus.title")}</h1>
        {siteId ? <RowsSkeleton /> : <PickASite />}
      </div>
    );
  }
  return <SiteMenus key={siteId} siteId={siteId} siteName={siteName} today={today} />;
}

function SiteMenus({
  siteId,
  siteName,
  today,
}: {
  siteId: string;
  siteName: string;
  /** The site's today (`useSiteToday`). */
  today: string;
}) {
  const { t, locale } = useTranslation();
  const open = useOpen();
  const { isPlatformAdmin, permissions } = useClaims();
  const canEdit = isPlatformAdmin || hasPermission(permissions, "menu:update");
  const canUpload = isPlatformAdmin || hasPermission(permissions, "menu:create");
  const menusQuery = useSiteMenus(siteId, false);
  const defaultMenu = useDefaultMenu();
  const { setLive, archiveWithUndo } = useMenuActions();
  const [settingsFor, setSettingsFor] = useState<Menu | null>(null);
  const [uploading, setUploading] = useState(false);
  const [watching, setWatching] = useState<string[]>([]);
  const menus = menusQuery.data ?? [];
  const label = (m: Menu) => menuLabel(t, locale, m);
  const fallback = defaultMenu && (
    <section className="mb-6">
      <GroupHeading label={t("menus.groups.default")} hint={t("menus.groups.defaultHint")} />
      <FallbackCard menu={defaultMenu} inUse={!menus.some((m) => runsAt(m, siteId, today))} />
    </section>
  );

  function toggle(menu: Menu, on: boolean) {
    const others = menus.filter((m) => m.id !== menu.id && runsAt(m, siteId, today));
    setLive.mutate(
      { menuId: menu.id, siteId, on },
      {
        onSuccess: () => {
          if (!on && others.length === 0) {
            toast(
              defaultMenu
                ? t("menus.list.nothingLive", { site: siteName, fallback: defaultMenu.name })
                : t("menus.list.nothingLiveNoFallback", { site: siteName }),
            );
          } else {
            toast(
              on
                ? t("menus.list.isLive", { name: label(menu) })
                : t("menus.list.switchedOff", { name: label(menu) }),
            );
          }
        },
      },
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium text-gray-900">{t("menus.title")}</h1>
          <p className="text-sm text-gray-500">{siteName}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => open("/menus/archive", t("menus.title"))}
          >
            {t("menus.list.archive")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => open("/menus/recipes", t("menus.title"))}
          >
            <List />
            {t("menus.list.allRecipes")}
          </Button>
          {canUpload && (
            <Button size="sm" onClick={() => setUploading(true)}>
              <Upload />
              {t("menus.uploadMenu")}
            </Button>
          )}
        </div>
      </div>

      {menusQuery.isPending ? (
        <RowsSkeleton />
      ) : menusQuery.isError ? (
        <LoadError onRetry={() => menusQuery.refetch()} />
      ) : menus.length === 0 ? (
        <>
          <div className="mb-6 flex flex-col items-center rounded-lg border border-black/[0.06] bg-white px-6 py-14 text-center">
            <Upload className="size-8 text-gray-300" />
            <p className="mt-3 text-sm font-medium text-gray-900">
              {t("menus.list.emptyTitle", { site: siteName })}
            </p>
            <p className="mt-1 max-w-sm text-sm text-gray-500">
              {t("menus.list.emptyDescription")}
            </p>
          </div>
          {fallback}
        </>
      ) : (
        GROUPS.map((group) => {
          const rows = sortGroup(
            group,
            menus.filter((m) => groupOf(m, menus, today) === group),
          );
          return (
            <Fragment key={group}>
              {group === "ended" && fallback}
              {rows.length > 0 && (
                <section className="mb-6">
                  <GroupHeading
                    label={t(GROUP_LABELS[group].label)}
                    hint={t(GROUP_LABELS[group].hint)}
                  />
                  <div className="divide-y divide-black/[0.06] overflow-hidden rounded-lg border border-black/[0.06] bg-white">
                    {rows.map((m) => (
                      <MenuRow
                        key={m.id}
                        menu={m}
                        menus={menus}
                        siteId={siteId}
                        today={today}
                        canEdit={canEdit}
                        pending={setLive.isPending && setLive.variables?.menuId === m.id}
                        onToggle={(on) => toggle(m, on)}
                        onSettings={() => setSettingsFor(m)}
                        onArchive={() => archiveWithUndo(m.id, label(m), runsAt(m, siteId, today))}
                      />
                    ))}
                  </div>
                </section>
              )}
            </Fragment>
          );
        })
      )}

      {uploading && (
        <UploadFlow
          siteId={siteId}
          siteName={siteName}
          menus={menus}
          today={today}
          onClose={() => setUploading(false)}
          onKeepReading={(menuId) => setWatching((ids) => [...ids, menuId])}
        />
      )}
      {watching.map((menuId) => (
        <ReadWatcher
          key={menuId}
          menuId={menuId}
          siteId={siteId}
          onEnded={() => setWatching((ids) => ids.filter((id) => id !== menuId))}
        />
      ))}

      {settingsFor && (
        <MenuSettingsDialog
          menu={settingsFor}
          menus={menus}
          live={runsAt(settingsFor, siteId, today)}
          today={today}
          onClose={() => setSettingsFor(null)}
          onArchive={() => {
            archiveWithUndo(settingsFor.id, label(settingsFor), runsAt(settingsFor, siteId, today));
            setSettingsFor(null);
          }}
        />
      )}
    </div>
  );
}

function useDefaultMenu(): DefaultMenu | null {
  const headers = useApiHeaders();
  const query = useQuery<DefaultMenu | null>({
    queryKey: ["default-menu-card"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/default-menu`, { headers });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to load the default menu");
      return (await res.json()).data;
    },
  });
  return query.data ?? null;
}

/** A read that carries on after the upload dialog closed: toasts when it ends (POS2-1456). */
function ReadWatcher({
  menuId,
  siteId,
  onEnded,
}: {
  menuId: string;
  siteId: string;
  onEnded: () => void;
}) {
  const { t, locale } = useTranslation();
  const queryClient = useQueryClient();
  const detail = useMenuDetail(menuId, siteId, stillReading);
  const told = useRef(false);
  const menu = detail.data;
  const ended = !!menu && !stillReading(menu);

  useEffect(() => {
    if (!menu || !ended || told.current) return;
    told.current = true;
    const name = menuLabel(t, locale, menu);
    if (menu.status === "failed") toast.error(t("menus.upload.readFailedToast", { name }));
    else toast.success(t("menus.upload.readToast", { name }));
    queryClient.invalidateQueries({ queryKey: ["menus"] });
    onEnded();
  }, [ended, menu, onEnded, queryClient, t, locale]);

  return null;
}

function GroupHeading({ label, hint }: { label: string; hint: string }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
      <span className="hidden text-xs text-gray-400 sm:inline">{hint}</span>
      <span className="h-px flex-1 bg-black/[0.06]" />
    </div>
  );
}

function subLine(
  t: TFunction,
  locale: Locale,
  menu: Menu,
  menus: Menu[],
  live: boolean,
  liveFrom: string | null,
  today: string,
) {
  if (isReading(menu)) return t("menus.list.reading", { file: menu.file_name });
  if (menu.status === "failed") return menu.error_message ?? t("menus.list.readFailed");
  if (!menu.display_name) return t("menus.list.notNamed");
  const time = serviceTime(menu);
  const prefix =
    menu.cadence === "always_on" ? "" : time ? `${time}. ` : `${t("menus.list.allDay")}. `;
  const predecessor = predecessorOf(menu, menus);
  if (predecessor && !live) {
    return prefix + t("menus.list.goesLiveAfter", { name: menuLabel(t, locale, predecessor) });
  }
  if (!live && liveFrom) {
    return prefix + t("menus.list.goesLiveOn", { date: shortDate(locale, liveFrom) });
  }
  if (!live && menu.starts_on && inDates(menu, today)) {
    return prefix + t("menus.list.switchedOffInDates");
  }
  return (
    prefix +
    t("menus.list.uploaded", {
      date: shortDate(locale, menu.created_at.slice(0, 10)),
      file: menu.file_name,
    })
  );
}

const MARK = "\u0000";

/** A translated line with one value in bold. The translator keeps the whole sentence. */
function withBold(
  t: TFunction,
  key: string,
  name: string,
  value: string,
  params: TParams = {},
): ReactNode {
  const [before, after] = t(key, { ...params, [name]: MARK }).split(MARK);
  return (
    <>
      {before}
      <b className="font-medium text-gray-900">{value}</b>
      {after}
    </>
  );
}

function whenLine(
  t: TFunction,
  locale: Locale,
  menu: Menu,
  live: boolean,
  today: string,
): ReactNode {
  if (menu.ends_on && menu.ends_on < today) {
    return withBold(t, "menus.list.ended", "date", shortDate(locale, menu.ends_on));
  }
  if (menu.cadence === "always_on") {
    return withBold(t, "menus.list.everyDay", "time", serviceTime(menu) ?? t("menus.list.allDay"));
  }
  const dates = datesLabel(locale, menu);
  if (!dates || !menu.starts_on) return t("menus.list.noDates");
  if (menu.starts_on > today) {
    return withBold(t, "menus.list.starts", "date", shortDate(locale, menu.starts_on));
  }
  if (!live) return withBold(t, "menus.list.notRunning", "dates", dates);
  const left = daysBetween(today, menu.ends_on!);
  return left === 0
    ? withBold(t, "menus.list.lastDay", "dates", dates)
    : withBold(t, "menus.list.daysLeft", "dates", dates, { count: left });
}

function MenuRow({
  menu,
  menus,
  siteId,
  today,
  canEdit,
  pending,
  onToggle,
  onSettings,
  onArchive,
}: {
  menu: Menu;
  menus: Menu[];
  siteId: string;
  today: string;
  canEdit: boolean;
  pending: boolean;
  onToggle: (on: boolean) => void;
  onSettings: () => void;
  onArchive: () => void;
}) {
  const { t, locale } = useTranslation();
  const openPage = useOpen();
  const reading = isReading(menu);
  const failed = menu.status === "failed";
  const ended = hasEnded(menu, today);
  const live = isLiveAt(menu, siteId);
  const label = menuLabel(t, locale, menu);
  const open = () => openPage(`/menus/${menu.id}`, t("menus.title"));

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => e.key === "Enter" && open()}
      className={`flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors duration-[120ms] ${
        reading ? "bg-amber-50/60 hover:bg-amber-50" : "hover:bg-black/[0.02]"
      }`}
    >
      <div className="flex w-8 shrink-0 justify-center">
        {reading ? (
          <Loader2 className="size-4 animate-spin text-amber-600" />
        ) : (
          <Switch
            checked={live && !ended}
            disabled={!canEdit || ended || pending}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={onToggle}
            aria-label={t("menus.list.liveToggle", { name: label })}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={`truncate text-sm font-medium ${
            reading ? "text-amber-800" : ended ? "text-gray-500" : "text-gray-900"
          }`}
        >
          {label}
        </p>
        <p
          className={`truncate text-xs ${
            reading ? "text-amber-700" : failed ? "text-red-600" : "text-gray-400"
          }`}
        >
          {menu.cut_off_after && !reading && (
            <span className="text-amber-700">{t("menus.list.cutOff")} </span>
          )}
          {menu.parts_reading.length > 0 && !reading && (
            <span className="text-amber-700">
              <Loader2 className="mr-1 inline size-3 animate-spin" />
              {t("menus.list.addingParts", { files: menu.parts_reading.join(", ") })}{" "}
            </span>
          )}
          {subLine(t, locale, menu, menus, live, liveFromAt(menu, siteId), today)}
        </p>
        <p className="mt-0.5 text-xs text-gray-500 sm:hidden">
          {whenLine(t, locale, menu, live, today)}
        </p>
      </div>
      <div className="hidden w-56 shrink-0 text-sm text-gray-500 sm:block">
        {whenLine(t, locale, menu, live, today)}
      </div>
      <div className="hidden w-12 shrink-0 justify-end sm:flex">
        <RecipeCount count={reading ? null : menu.recipe_count} />
      </div>
      {canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("menus.list.options", { name: label })}
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="size-4" />
            </button>
          </DropdownMenuTrigger>
          {/* Portalled, but React still bubbles its clicks to the row. */}
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {/* A read places the dishes on the schedule it started with (D93). */}
            <DropdownMenuItem disabled={reading} onClick={onSettings}>
              <Settings2 className="mr-2 size-3.5" />
              {t("menus.list.editSettings")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={open}>
              <List className="mr-2 size-3.5" />
              {t("menus.list.viewRecipes")}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={reading}
              onClick={onArchive}
              className="text-red-600 focus:text-red-600"
            >
              <Archive className="mr-2 size-3.5" />
              {t("menus.archive")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

/** Opens the default menu's recipes, read-only, for everyone; it is configured from the admin pages. */
function FallbackCard({ menu, inUse }: { menu: DefaultMenu; inUse: boolean }) {
  const { t } = useTranslation();
  const openPage = useOpen();
  const open = () => openPage("/menus/default", t("menus.title"));
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => e.key === "Enter" && open()}
      className="flex cursor-pointer items-center gap-3 rounded-lg border border-black/[0.06] bg-white px-4 py-3 transition-colors duration-[120ms] hover:bg-black/[0.02]"
    >
      <ShieldCheck className="size-4 shrink-0 text-gray-400" />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
          {menu.name}
          {inUse && (
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-700">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              {t("menus.fallback.inUse")}
            </span>
          )}
        </p>
        <p className="text-xs text-gray-400">
          {inUse
            ? t("menus.fallback.inUseHint", { count: menu.recipe_count })
            : t("menus.fallback.dormantHint", { count: menu.recipe_count })}
        </p>
      </div>
    </div>
  );
}
